import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import { config } from '../config'
import { bearerToken, endSession } from '../auth/session'

// The seam the generated client is grafted onto. `authoringApi.ts` calls
// `baseApi.injectEndpoints`, so everything about transport, credentials
// and cache identity is decided here and nowhere else.

/**
 * A document's body as text, and a refusal's as the problem it is.
 *
 * For the reads that answer something other than JSON — the OpenAPI
 * documents are YAML — which the default handler would fail to parse.
 */
export const textUnlessProblem = (response: Response): Promise<unknown> => (response.ok ? response.text() : response.json())

/**
 * Cache tags.
 *
 * `Component` is keyed by `(tenant, id)` — the pair the model addresses a
 * component by — because two tenants may hold the same bare id and a
 * cache that keyed on the id alone would serve one tenant's document
 * under the other's route.
 *
 * **`Manifest` is keyed by tenant, and it used to have no tag at all.**
 * The manifest was a build-time constant while every tenant got the same
 * answer; a tenant may now define component types of its own, so it is
 * per tenant and it changes — when a definition is written. That is the
 * one thing that invalidates it, which is why it is a tag rather than a
 * poll or a refetch on every command.
 */
export const tagTypes = [
  'Component',
  'ComponentList',
  'Manifest',
  'Tenants',
  'Usages',
  'Publications',
  'Webhooks',
  'WebhookDeliveries',
  'Submissions',
  'TenantDomains',
  // A site's visit counts, which an operator's bind or unbind moves.
  'Traffic',
  // The operator surface's. Kept apart from `Tenants`, which is the
  // caller's own memberships on the authoring surface.
  'OperatorTenants',
  'OperatorTenant',
  'Administrators',
  'Domains',
  'Billing',
  'BillingOverview',
  'Plans',
  'TenantTraffic',
  'TrafficConnections',
] as const

/** One tenant's manifest as a cache key. */
export const manifestTag = (tenant: string) => ({ type: 'Manifest' as const, id: tenant })

/** One site's visit counts as a cache key, which the operator surface's writes to its binding move. */
export const siteTrafficTag = (tenant: string, website: string) => ({ type: 'Traffic' as const, id: `${tenant}/${website}` })

/** `(tenant, id)` as one cache key. */
export const componentTag = (tenant: string, id: string) => ({ type: 'Component' as const, id: `${tenant}/${id}` })

const rawBaseQuery = fetchBaseQuery({
  baseUrl: config.apiBaseUrl,
  prepareHeaders: async (headers) => {
    // No header at all in the local loop, rather than an empty or fake
    // one: the surface runs credential-free there and a header would be a
    // fiction the next reader has to disprove.
    const token = await bearerToken()
    if (token) headers.set('Authorization', `Bearer ${token}`)
    return headers
  },
})

/**
 * RFC 9457 problem details, which both surfaces answer with.
 *
 * One error shape over the whole API is what lets a single renderer
 * handle a 400 from the model and a 403 from the membership check without
 * knowing which it is looking at.
 */
export interface Problem {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
}

export function isProblem(value: unknown): value is Problem {
  return (
    typeof value === 'object' &&
    value !== null &&
    'title' in value &&
    'status' in value &&
    typeof (value as Problem).status === 'number'
  )
}

/**
 * The message to show a human for a failed request.
 *
 * `detail` first, because the surface writes it to say what went wrong
 * with *this* request — an unknown intent, a containment rule, a link
 * path that resolves to nothing — while `title` only names the class.
 */
export function problemMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (typeof error === 'object' && error !== null && 'status' in error) {
    const fetchError = error as FetchBaseQueryError
    if (fetchError.status === 'FETCH_ERROR') {
      // An unreachable host and a refused origin arrive here as the same
      // error — the browser will not tell script which it was — so the
      // message names the loop it is actually running in rather than
      // guessing. Against a deployed stage the likely cause is the
      // second: this origin is not in that stage's `AdminOrigins`.
      return config.authMode === 'none'
        ? 'The API could not be reached. Is the local stack running (`task dev`)?'
        : `The API at ${config.apiBaseUrl} could not be reached, or would not answer ${window.location.origin}.`
    }
    if (isProblem(fetchError.data)) {
      return fetchError.data.detail ?? fetchError.data.title
    }
    if (typeof fetchError.status === 'number') {
      return `The request failed (${fetchError.status}).`
    }
  }
  return fallback
}

/** The HTTP status of a failed request, when there was one. */
export function statusOf(error: unknown): number | null {
  if (typeof error !== 'object' || error === null || !('status' in error)) return null
  const { status, data } = error as { status: unknown; data?: unknown }
  // The transport's own status when there is one; otherwise the problem
  // document's, which is the same number the surface put in the body.
  if (typeof status === 'number') return status
  return isProblem(data) ? data.status : null
}

/**
 * The transport, plus the one thing a failed request can mean about the
 * session rather than about the request.
 *
 * A 401 is the edge refusing the credential — expired beyond refresh,
 * revoked, or an identity that was deactivated — and no retry mends it.
 * Dropping the session is what puts the sign-in screen back; without it
 * the app sits on a screen whose every query fails and whose only control
 * that leads anywhere is the browser's reload button.
 *
 * **A 403 deliberately does not do this.** That is an identity the pool
 * authenticated and this surface will not serve — no `admin` group, or no
 * membership in the tenant — and signing it out would hide the one
 * message that says so behind a login page it can already get past.
 *
 * Only in `cognito` mode: the local loop holds no session to end, and a
 * 401 there means the surface was misconfigured, which is worth seeing.
 */
const baseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions)
  if (config.authMode === 'cognito' && result.error?.status === 401) endSession()
  return result
}

export const baseApi = createApi({
  reducerPath: 'authoring',
  baseQuery,
  tagTypes,
  // Every endpoint arrives by injection from the generated client. This
  // list stays empty so that adding an operation to the contract and
  // regenerating is the whole change.
  endpoints: () => ({}),
})
